import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight, Info, MapPin } from '@phosphor-icons/react';
import { Spot } from 'utils/constants';
import {
    LatLng,
    RateKey,
    businessAccountBookingUrl,
    formatDistanceAway,
    formatSpotAddress,
    getRateOptions,
    pickRateOption,
} from 'utils/spotSearch';
import MonthlyAccountPicker, { MonthlyAccount } from './MonthlyAccountPicker';
import styles from './FindParking.module.css';

export interface SpotCardResultProps {
    spot: Spot;
    rentalType: string;
    origin?: LatLng;
    /** Hovered, on the card or its pin. */
    active: boolean;
    /** Pin clicked on the map, or this card tapped. */
    selected: boolean;
    onHover: (spotPk: Spot['pk'] | null) => void;
    /** Card tapped somewhere other than a link, tab, or button. */
    onSelect: (spot: Spot) => void;
}

const HOURLY_NOTICE =
    'Hourly rates are subject to change based on the time of day. Please check the rate at the time of booking to ensure accurate pricing.';

const SpotCardResult: React.FC<SpotCardResultProps> = ({
    spot,
    rentalType,
    origin,
    active,
    selected,
    onHover,
    onSelect,
}) => {
    const options = useMemo(() => getRateOptions(spot), [spot]);
    const [selectedKey, setSelectedKey] = useState<RateKey | undefined>(() => pickRateOption(options, rentalType)?.key);

    // Reset the tab only for a different spot or URL rental type, not when results refresh with
    // the same spot (pickRateOption already falls back if the chosen key stops being offered).
    useEffect(() => {
        setSelectedKey(pickRateOption(getRateOptions(spot), rentalType)?.key);
    }, [spot.pk, rentalType]);

    const [monthlyAccount, setMonthlyAccount] = useState<MonthlyAccount>('individual');
    const rate = pickRateOption(options, selectedKey);
    const distance = formatDistanceAway(origin, spot);
    const isBusinessBooking = Boolean(rate && rate.key === 'monthly' && monthlyAccount === 'business');
    const bookingUrl = isBusinessBooking ? businessAccountBookingUrl(spot) : rate?.bookingUrl;
    const cardRef = useRef<HTMLDivElement>(null);

    // Selecting a pin brings its card into view in the results list.
    useEffect(() => {
        if (selected && cardRef.current) {
            cardRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
    }, [selected]);

    return (
        <div
            ref={cardRef}
            className={`${styles.card} ${active ? styles.cardActive : ''} ${selected ? styles.cardSelected : ''}`}
            aria-current={selected ? 'true' : undefined}
            onMouseEnter={() => onHover(spot.pk)}
            onMouseLeave={() => onHover(null)}
            onClick={(event) => {
                const target = event.target as HTMLElement;
                if (target.closest('a, button')) return;
                onSelect(spot);
            }}
        >
            <div className={styles.cardTop}>
                {spot.photo_url ? (
                    <img className={styles.photo} src={spot.photo_url} alt={`${spot.name} property`} />
                ) : (
                    <div className={styles.photoPlaceholder} aria-hidden="true">
                        <MapPin size={20} />
                    </div>
                )}
                <div className={styles.cardBody}>
                    <h3 className={styles.name}>{spot.name}</h3>
                    <div className={styles.address}>{formatSpotAddress(spot)}</div>
                    {distance && (
                        <div className={styles.metaRow}>
                            <span className={styles.metaItem}>
                                <MapPin size={14} />
                                {distance}
                            </span>
                        </div>
                    )}
                </div>
            </div>

            {options.length > 0 && (
                <div className={styles.tabs} role="tablist" aria-label="Rate options">
                    {options.map((option) => {
                        const isSelected = option.key === rate?.key;
                        return (
                            <button
                                key={option.key}
                                type="button"
                                role="tab"
                                aria-selected={isSelected}
                                className={`${styles.tab} ${isSelected ? styles.tabActive : ''}`}
                                onClick={() => setSelectedKey(option.key)}
                            >
                                {option.label}
                            </button>
                        );
                    })}
                </div>
            )}

            {rate && (
                <>
                    <div className={styles.priceRow}>
                        <div className={styles.priceGroup}>
                            <span className={styles.price}>{rate.price}</span>
                            <span className={styles.priceUnit}>{rate.unit}</span>
                        </div>
                        {rate.note && <span className={styles.priceNote}>{rate.note}</span>}
                    </div>

                    {rate.moving && (
                        <div className={styles.tierTable}>
                            <div className={styles.tierHeader}>
                                <span>You stay</span>
                                <span>You pay</span>
                            </div>
                            {rate.moving.tiers.map((tier) => (
                                <div key={tier.time} className={styles.tierRow}>
                                    <span>{tier.time}</span>
                                    <span className={styles.tierPrice}>{tier.price}</span>
                                </div>
                            ))}
                            {rate.moving.dailyMax && (
                                <div className={styles.tierRow}>
                                    <span>Daily max</span>
                                    <span className={styles.tierPrice}>{rate.moving.dailyMax}</span>
                                </div>
                            )}
                        </div>
                    )}
                    {rate.terms && <p className={styles.rateTerms}>{rate.terms}</p>}

                    {rate.key === 'monthly' && (
                        <MonthlyAccountPicker spot={spot} value={monthlyAccount} onChange={setMonthlyAccount} />
                    )}

                    {rate.showHourlyNotice && (
                        <div className={styles.notice}>
                            <Info size={16} weight="fill" className={styles.noticeIcon} />
                            <span>{HOURLY_NOTICE}</span>
                        </div>
                    )}

                    <div className={styles.actions}>
                        <a
                            className={styles.neutralButton}
                            href={rate.infoUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            More Info
                        </a>
                        <a
                            className={`${styles.primaryButton} ${
                                isBusinessBooking ? styles.primaryButtonBusiness : ''
                            }`}
                            href={bookingUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Book Now
                            <ArrowRight size={16} />
                        </a>
                    </div>
                </>
            )}
        </div>
    );
};

export default SpotCardResult;
